<script>
import { getContext, setContext } from "svelte";
import { MENU_BAR_KEY, MENU_PANEL_KEY } from "./menuContext.js";
import { focusItem, getEnabledItems } from "./menuKeyboard.js";
import { isMenuAllowed } from "@app/store/permissionStore.svelte.js";

/**
 * @type {{
 *   id: string,
 *   label?: string,
 *   icon?: string,
 *   ariaLabel?: string,
 *   alignEnd?: boolean,
 *   children: import('svelte').Snippet,
 * }}
 */
let {
    id,
    label = "",
    icon = "",
    ariaLabel = "",
    alignEnd = false,
    children,
} = $props();

const menuBar = getContext(MENU_BAR_KEY);
const panelState = $state({ openChildId: /** @type {string | null} */ (null) });
setContext(MENU_PANEL_KEY, {
    get menuId() {
        return id;
    },
    get openChildId() {
        return panelState.openChildId;
    },
    openChild(childId) {
        panelState.openChildId = childId;
    },
    closeChild(childId) {
        if (panelState.openChildId === childId) {
            panelState.openChildId = null;
        }
    },
});

/** @type {HTMLAnchorElement | null} */
let triggerEl = null;
let isOpen = $derived(menuBar.isOpen(id));
let hoverOpenedAt = 0;

/** @type {import('svelte/attachments').Attachment<HTMLAnchorElement>} */
function attachTrigger(node) {
    triggerEl = node;
    menuBar.register(id, node);
    return () => {
        menuBar.unregister(id);
        triggerEl = null;
    };
}

$effect(() => {
    if (!isOpen && panelState.openChildId !== null) {
        panelState.openChildId = null;
    }
});

function toggle(event) {
    event.preventDefault();
    if (Date.now() - hoverOpenedAt < 400) {
        return;
    }
    if (isOpen) {
        menuBar.closeAll();
        return;
    }
    menuBar.open(id);
}

function onMouseEnter() {
    if (menuBar.isAnyOpen() && !isOpen) {
        menuBar.open(id);
        hoverOpenedAt = Date.now();
    }
}
</script>

{#if isMenuAllowed(id)}
<li class={["nav-item", "dropdown", isOpen && "show"]} role="none">
    <a
        {@attach attachTrigger}
        class="dropdown-toggle nav-link"
        href="#!/"
        role="menuitem"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={ariaLabel || undefined}
        id="{id}Button"
        onclick={toggle}
        onmouseenter={onMouseEnter}
        onkeydown={(event) => {
            if (event.key === "ArrowDown" && !isOpen) {
                event.preventDefault();
                menuBar.open(id);
                requestAnimationFrame(() => {
                    const panel = triggerEl
                        ?.closest(".nav-item")
                        ?.querySelector(":scope > [role='menu']");
                    focusItem(getEnabledItems(panel)[0]);
                });
            }
        }}>
        {#if icon}
            <i class="bi {icon}" aria-hidden="true"></i>
        {/if}
        {#if label}
            {#if icon}<span class="ms-1">{label}</span>{:else}{label}{/if}
        {/if}
    </a>
    <div
        class={["dropdown-menu", isOpen && "show", alignEnd && "dropdown-menu-end"]}
        role="menu"
        aria-labelledby="{id}Button">
        {@render children()}
    </div>
</li>
{/if}

<style>
.dropdown-menu {
    overflow: visible;
    min-width: 14rem;
}

/* Items hidden by an IFrame toolbar configuration can strand dividers. */
.dropdown-menu :global(hr.dropdown-divider:first-child),
.dropdown-menu :global(hr.dropdown-divider:last-child),
.dropdown-menu :global(hr.dropdown-divider + hr.dropdown-divider) {
    display: none;
}

.dropdown-menu :global(.dropdown-submenu:not(:has(.cc-menu-item))) {
    display: none;
}
</style>
